import {computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import errorToast from "../../components/toast/ErrorToast";
import {default as handleSuccessToast} from "../../components/toast/SuccessToast";
import DeliverySiteOnboardingRequestForm from "../../forms/fields/DeliverySiteOnboardingRequestForm";
import {DELIVERY_SITE_ONBOARDING_EDIT_PATH} from "../../routes";
import Jurisdictions from "../../domain/Jurisdictions";

export default class DeliverySiteOnboardingFeature {

    constructor(application, navigator, customerBusinessName = null, deliverySiteOnboardingExternalId = null, readonly = false) {
        this._application = application;
        this._navigator = navigator;
        this._customerBusinessName = customerBusinessName;
        this._deliverySiteOnboardingExternalId = deliverySiteOnboardingExternalId;
        this._loaded = false;
        this._form = null;
        this._deliverySiteOnboarding = null;
        this._isEditing = !!deliverySiteOnboardingExternalId;
        this._readonly = readonly;

        makeObservable(this, {
            _loaded: observable,
            loaded: computed,
            _form: observable,
            _deliverySiteOnboarding: observable,
            form: computed,
            isFormValid: computed,
        })
    }

    async load() {
        if (this._isEditing) {
            await this._loadForEdition()
            if (!this._form) {
                this._form = DeliverySiteOnboardingRequestForm.newEmpty(this)
            }
        } else {
            this._form = DeliverySiteOnboardingRequestForm.newEmpty(this)
        }
        this._loaded = true
    }

    get loaded() {
        return this._loaded;
    }

    get form() {
        return this._form;
    }

    get isFormValid() {
        return this._form && this._form.isValid;
    }

    async createDeliverySiteOnboarding() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._deliverySiteOnboarding = response.deliverySiteOnboarding();
                handleSuccessToast('Punto de entrega creado correctamente.');
                const path = DELIVERY_SITE_ONBOARDING_EDIT_PATH.replace(':id', this._deliverySiteOnboarding.id);
                this._navigator(path);
            },
            handlesError: (response) => {
                if (response.code === 'user_does_not_have_required_permission_error') {
                    errorToast('No existe el cliente buscado');
                } else {
                    errorToast('No se pudo crear el punto de entrega.');
                }
            }
        });
        return this._application.apiClient().createDeliverySiteOnboarding(
            this._customerBusinessName,
            this._form.formEntity(),
            responseHandler
        );
    }

    async formChanged(deliverySiteOnboardingRequest) {
        if (!this._isEditing || !this.canEditDeliverySitesOnboardingRequests()) {
            return;
        }

        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                handleSuccessToast('Punto de entrega actualizado correctamente.');
                this._deliverySiteOnboarding = response.deliverySiteOnboarding();
            },
            handlesError: (response) => {
                if (response.code === 'user_does_not_have_required_permission_error') {
                    errorToast('No existe el punto de entrega buscado');
                } else {
                    errorToast('No se pudieron guardar los datos.');
                }
            }
        });
        return this._application.apiClient().editDeliverySiteOnboarding(
            this._deliverySiteOnboardingExternalId,
            deliverySiteOnboardingRequest,
            responseHandler
        );
    }

    async editOrCreateDeliverySiteOnboardingAuthorizations() {
        if (!this._isEditing || this._readonly) {
            return;
        }

        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._deliverySiteOnboarding = response.deliverySiteOnboarding();
                handleSuccessToast("Se han creado las solicitudes de autorización para las áreas correspondientes");
            },
            handlesError: (response) => {
                if (response.code === 'user_does_not_have_required_permission_error') {
                    errorToast('No existe el punto de entrega buscado');
                } else {
                    errorToast('No se pudieron crear las solicitudes de autorización');
                }
            }
        });

        return this._application.apiClient().editOrCreateDeliverySiteOnboardingAuthorizations(
            this._deliverySiteOnboardingExternalId,
            responseHandler
        );
    }

    async _loadForEdition() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._deliverySiteOnboarding = response.deliverySiteOnboarding();
                this._form = DeliverySiteOnboardingRequestForm.newFor(this._deliverySiteOnboarding.request, this);
            },
            handlesError: (response) => {
                if (response.code === 'user_does_not_have_required_permission_error') {
                    errorToast('No existe el punto de entrega buscado');
                } else {
                    errorToast('No se pudo obtener el punto de entrega.');
                }
            }
        });
        return this._application.apiClient().getDeliverySiteOnboarding(
            this._deliverySiteOnboardingExternalId,
            responseHandler
        );
    }

    canEditDeliverySitesOnboardingRequests() {
        if (this._readonly) {
            return false;
        }

        const isCreating = !this._deliverySiteOnboarding;
        return isCreating || this._hasNoAuthorizationsYet() || this._hasAnyAuthorizationRequiringChanges();
    }

    canSendToAuthorization() {
        return this.canEditDeliverySitesOnboardingRequests() && this.isFormValid;
    }

    _hasAnyAuthorizationRequiringChanges() {
        if (!this._deliverySiteOnboarding || !this._deliverySiteOnboarding.authorizations) {
            return false;
        }

        const authorizations = this._deliverySiteOnboarding.authorizations;
        return authorizations.taxAuthorizationState === 'Changes Required'
            || authorizations.logisticsAuthorizationState === 'Changes Required';
    }

    _hasNoAuthorizationsYet() {
        if (!this._deliverySiteOnboarding) {
            return false;
        }

        const authorizations = this._deliverySiteOnboarding.authorizations;
        if (!authorizations) {
            return true;
        }

        return this._isEmptyAuthorizationState(authorizations.taxAuthorizationState)
            && this._isEmptyAuthorizationState(authorizations.logisticsAuthorizationState);
    }

    _isEmptyAuthorizationState(value) {
        if (value === null || value === undefined || value === false) {
            return true;
        }

        if (typeof value === 'string') {
            return value.trim() === '';
        }

        return false;
    }

    zones() {
        return [
            'CABA',
            'OESTE',
            'SUR',
            'NORTE',
            'NORTE EXTENDIDO PILAR',
            'NORTE EXTENDIDO ESCOBAR',
            'ROSARIO',
            'CORDOBA',
        ];
    }

    frequencies() {
        return [
            'LUNES',
            'MARTES',
            'MIÉRCOLES',
            'JUEVES',
            'VIERNES',
            'SÁBADO',
            'SIN DIA FIJOS',
        ];
    }

    jurisdictions() {
        return Jurisdictions.names();
    }

    jurisdictionsFieldOptions() {
        return this.optionsFor(this.jurisdictions());
    }

    zonesFieldOptions() {
        return this.optionsFor(this.zones());
    }

    frequenciesFieldOptions() {
        return this.optionsFor(this.frequencies());
    }

    optionsFor(values) {
        return values.map(value => {
            return {label: value, value: value}
        });
    }

    zoneFrequencyMapping() {
        return {
            'CABA': ['MARTES', 'JUEVES', 'SABADO'],
            'OESTE': ['LUNES', 'JUEVES'],
            'SUR': ['MIERCOLES', 'VIERNES'],
            'NORTE': ['LUNES', 'MIERCOLES', 'VIERNES'],
            'NORTE EXTENDIDO': ['MARTES', 'VIERNES'],
            'NORTE EXTENDIDO PILAR': ['MIERCOLES', 'SABADO'],
            'NORTE EXTENDIDO ESCOBAR': ['MARTES', 'VIERNES'],
            'ROSARIO': ['VIERNES', 'SABADO'],
            'CORDOBA': ['SIN DIAS FIJOS'],
        };
    }

    chosenZoneFrequency(deliverySiteOnboardingRequestForm) {
        const chosenZone = deliverySiteOnboardingRequestForm.zone();
        if (chosenZone) {
            return this.zoneFrequencyMapping()[chosenZone];
        }
        return [];
    }
}
